import { zodResolver } from '@hookform/resolvers/zod'
import { DialogContent } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import FormDialogActions from '@/components/feedback/FormDialogActions'
import SelectFieldController from '@/components/inputs/SelectFieldController'
import { getGenericStartPrequalificationSchema } from '@/features/activationGroup/schemas'
import type { GenericStartPrequalification } from '@/features/activationGroup/types'

type GenericStartPrequalificationDialogFormProps = {
  activationTriggerChannels: Array<{ value: number; label: string }>
  patternUuid: string
  isLoading: boolean
  onCancel: () => void
  onSubmit: (data: GenericStartPrequalification) => void
}

/**
 * Contains all the logic related to the form to start a new prequalification.
 *
 * It is used within the StartPrequalificationForm component.
 */
const GenericStartPrequalificationDialogForm = ({
  activationTriggerChannels,
  patternUuid,
  isLoading,
  onCancel,
  onSubmit,
}: GenericStartPrequalificationDialogFormProps) => {
  const { t } = useTranslation()
  const form = useForm<GenericStartPrequalification>({
    defaultValues: {
      patternUuid,
    },
    resolver: zodResolver(getGenericStartPrequalificationSchema(t)),
  })

  function handleSubmit(data: GenericStartPrequalification) {
    onSubmit(data)
  }

  return (
    <FormProvider {...form}>
      <form
        noValidate
        aria-label={t('activation_groups.start_prequalification_form.title')}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SelectFieldController
            required
            id="activation-trigger-channel"
            label={t('activation_groups.start_prequalification_form.portfolio_channel_label')}
            name="activationTriggerChannel"
            options={activationTriggerChannels.map((channel) => ({
              value: channel.value,
              label: channel.label,
              id: 'Channel-' + channel.value,
            }))}
          />
        </DialogContent>

        <FormDialogActions isLoading={isLoading} onCancel={onCancel} />
      </form>
    </FormProvider>
  )
}

export default GenericStartPrequalificationDialogForm
